import React, { useState, useRef, useEffect } from 'react';

/**
 * Audio Player Component
 * Plays audio with playback controls
 */
const AudioPlayer = ({ src }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(30);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
      const handleLoadedMetadata = () => setDuration(audio.duration || 30);

      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);

      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, []);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      height: '100%',
      minHeight: '100px'
    }}>
      <audio
        ref={audioRef}
        src={src}
        onEnded={handleEnded}
        style={{ display: 'none' }}
      />

      <div className="d-flex align-items-center gap-3">
        <button
          onClick={togglePlay}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            border: 'none',
            backgroundColor: isPlaying ? '#6c757d' : '#667eea',
            color: '#fff'
          }}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          <i className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`} style={{ fontSize: '1.5rem' }}></i>
        </button>

        <div style={{ flex: 1 }}>
          <div
            onClick={handleSeek}
            style={{
              height: '6px',
              backgroundColor: '#e2e8f0',
              borderRadius: '3px',
              cursor: 'pointer',
              position: 'relative'
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${progress}%`,
                backgroundColor: '#667eea',
                borderRadius: '3px',
                transition: 'width 0.1s ease'
              }}
            />
          </div>
          <div className="d-flex justify-content-between mt-1">
            <span style={{ fontSize: '0.75rem', color: '#a0aec0' }}>{formatTime(currentTime)}</span>
            <span style={{ fontSize: '0.75rem', color: '#a0aec0' }}>{formatTime(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
